import { DrawerProps } from 'antd/lib/drawer';
import ErrorBoundary from '@copilot/common/components/containers/errorBoundary';
import { ContactConnectionActions } from '@copilot/common/store/actions';
import { MeetingDetails } from '@copilot/data/responses/interface';
import { useBookMeeting } from '../data';
import { PipelineStateNames } from '@copilot/common/components/drawer/wrappers/contact/data/hooks';
import ContactDrawerLayout from '@copilot/common/components/drawer/wrappers/contact/layout';
import ContactDrawerInformationPanelSmart from '@copilot/common/components/drawer/wrappers/contact/contactDrawerInformationPanelSmart';
import { StaticBaseDrawer } from './styles';
import { CloseDrawerButton, saveConnectionTagsAsOrgMember, saveTagsAsAdmin } from './util';
import isNil from 'lodash/isNil';
import ThreadNavigator from './threadNavigator';
import ContactDrawerTabbedContentSmart from '../tabbedContent/contactDrawerTabbedContentSmart';
import styles from './drawer.module.less';
import { ContactDrawerTabs } from '../tabbedContent/constants';
import { calcAutomatedSteps } from '@copilot/common/pages/inbox/ui/component/automationHelpers';
import { convertContactTags } from '@copilot/common/pages/connections';
import { DRAWER_CONTAINER_TRACKING_ID } from '@copilot/common/tracking/userpilotEventConsts';
import { useContactDrawerState } from './useContactDrawerState';

type Props = DrawerProps & {
    /** id of contact */
    id: string;
    /** index of thread */
    threadIdx: number;
    /** id of orgMember */
    memberId: string;
    /** id of thread */
    threadId: string;
    /** drawer tab to open */
    tab?: ContactDrawerTabs;
    onClose?: () => void;
    /** handler for visited threads; will be called before onClose */
    visitedThreadsHandler?: (threadIds: string[]) => void;
    /** handler for threads with new messages sent; will be called before onClose */
    messageSentHandler?: () => void;
    /**
     * whether the drawer is open from new inbox container that uses saga
     * Dictates control flow on tag updates
     * TODO: Clean up logics for inbox - https://cassia.atlassian.net/browse/COPILOT-3079
     */
    isSagaInbox?: boolean;
};

/**
 * Drawer for contact
 * @param props
 */
function ContactDrawer({
    id: initContactId,
    threadIdx: initThreadIdx,
    memberId: initMemberId,
    threadId: initThreadId,
    tab,
    isSagaInbox,
    onClose,
    visitedThreadsHandler,
    messageSentHandler,
}: Props) {
    const {
        contactId,
        threadIdx,
        threadId,
        visitedThreadIds,
        hasSentMessage,
        messages,
        automatedSteps,
        selectedMemberId,
        numThreads,
        contactConnection,
        activeAdmin,
        isOrgAdmin,
        isOwner,
        orgId,
        updateProspectDrawerTracking,
        isNewProspectDrawerEnabled,
        selectedContactConnection,
        contactProfile,
        contactTags,
        conversation,
        isMessagesLoading,
        orgContactData,
        setThreadIdx,
        setThreadId,
        setVisitedThreadIds,
        setHasSentMessage,
        setAutomatedSteps,
        setSelectedMemberId,
        dispatch,
        setContactId,
        reminderProps,
        pipelineStates,
        flagStates,
        reminders,
        selectedCampaignConnection,
        setContactTags,
    } = useContactDrawerState({
        initContactId,
        initThreadIdx,
        initThreadId,
        initMemberId,
    });

    const [handleMeetingUpdate, bookMeetingMutationResult] = useBookMeeting(
        isNewProspectDrawerEnabled,
        selectedContactConnection?.id,
        onMeetingBookedUpdate
    );

    function onMeetingBookedUpdate(connectionId: string, bookedResult: MeetingDetails) {
        const contactConnectionId = contactConnection?.id;
        if (isNil(contactConnectionId)) return;
        dispatch(
            ContactConnectionActions.updateOne({
                id: contactConnectionId,
                connections: contactConnection?.connections.map((con) => {
                    if (con.id === connectionId) return { ...con, meeting: bookedResult };
                    else return con;
                }),
            })
        );
    }

    function onSaveTags(tags: string[]): void {
        updateProspectDrawerTracking({ buttonClicked: 'Save Tags' });
        if (contactConnection) {
            const csOrgId = activeAdmin ? orgId : null;
            if (isOrgAdmin && selectedContactConnection?.id) {
                saveTagsAsAdmin({
                    csOrgId,
                    selectedConnection: selectedContactConnection,
                    contactTags: tags,
                    contactConnection,
                    dispatch,
                    isSagaInbox,
                    convertContactTags,
                    threadId,
                });
            } else {
                saveConnectionTagsAsOrgMember({
                    dispatch,
                    csOrgId,
                    contactTags: tags,
                    threadId,
                    convertContactTags,
                    contactConnection,
                    memberId: selectedMemberId,
                });
            }
        }
    }

    function onGoToThread(newIdx: number) {
        if (isNil(messages.data)) return;
        if (newIdx >= 0 && newIdx < messages.data?.length) {
            setThreadIdx(newIdx);
            setSelectedMemberId(messages.data[newIdx].orgMemberId);
            setContactId(messages.data[newIdx].contactId);
            const newThreadId = messages.data[newIdx].threadId;
            setThreadId(newThreadId);
            setAutomatedSteps(calcAutomatedSteps(messages.data[newIdx].nextNodes));
            if (!visitedThreadIds.has(newThreadId)) {
                setVisitedThreadIds(new Set([...visitedThreadIds, newThreadId]));
            }
        }
    }

    function closeDrawer() {
        visitedThreadsHandler?.(Array.from(visitedThreadIds.values()));
        if (hasSentMessage) messageSentHandler?.();
        onClose?.();
    }

    return (
        <StaticBaseDrawer
            closable={false}
            width={flagStates.width}
            contentWrapperStyle={{ overflow: 'visible' }}
            {...{ currThreadId: threadId, tab, isSagaInbox, onClose }}
            className={styles.drawerBody}
            data-tracking-id={DRAWER_CONTAINER_TRACKING_ID}
        >
            <CloseDrawerButton onClose={closeDrawer} />
            {!isNil(threadIdx) && (
                <ThreadNavigator
                    currThreadIdx={threadIdx}
                    onGoToThread={onGoToThread}
                    numThreads={numThreads}
                />
            )}

            <ContactDrawerLayout>
                <ContactDrawerLayout.LeftPanel>
                    <ContactDrawerInformationPanelSmart
                        contactId={contactId}
                        contactTags={contactTags}
                        selectedOrgMemberId={selectedMemberId}
                        setContactTags={setContactTags}
                        contactConnection={contactConnection}
                        handleMemberSelect={setSelectedMemberId}
                        isMeetingBooked={selectedContactConnection?.meeting.booked ?? false}
                        isMeetingLoading={bookMeetingMutationResult?.loading ?? false}
                        isReminderLoading={reminders.loading}
                        handleMeetingClick={handleMeetingUpdate}
                        reminderProps={reminderProps}
                        contactAlias={contactProfile?.alias}
                        profileId={contactProfile?.id}
                        isOpenProfile={contactProfile?.isOpenProfile}
                        pipelineProps={{
                            hasMessaged: pipelineStates[PipelineStateNames.Messaged],
                            hasReplied: pipelineStates[PipelineStateNames.Replied],
                            hasBooked: pipelineStates[PipelineStateNames.Meeting],
                        }}
                        isOwner={isOwner}
                        onSaveTags={onSaveTags}
                        useOrgContactData={orgContactData}
                    />
                </ContactDrawerLayout.LeftPanel>
                <ContactDrawerLayout.RightPanel>
                    <ErrorBoundary>
                        <ContactDrawerTabbedContentSmart
                            contactId={contactId}
                            isMessagesLoading={isMessagesLoading}
                            isNewProspectDrawerEnabled={isNewProspectDrawerEnabled}
                            conversation={conversation}
                            contactConnection={contactConnection}
                            orgId={orgId}
                            tab={tab}
                            selectedConnection={selectedContactConnection}
                            contactProfile={contactProfile}
                            isOwner={isOwner}
                            automatedSteps={automatedSteps}
                            onMessageSent={() => setHasSentMessage(true)}
                            communicationStyles={selectedCampaignConnection?.communicationStyles}
                        />
                    </ErrorBoundary>
                </ContactDrawerLayout.RightPanel>
            </ContactDrawerLayout>
        </StaticBaseDrawer>
    );
}

export default ContactDrawer;
